import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { logError } from '@cortex/faro';
import { Badge } from '../../../ui/Badge/index.js';
import { Button } from '../../../ui/Button/index.js';
import { useToast } from '../../../ui/Toast/index.js';
import { useRecordContentDownload } from '../../../../hooks/useSnsContentGenerator.js';
import type {
  GeneratedContentImages,
  GeneratedImagePage,
} from '../../../../hooks/useSnsContentGenerator.js';

export interface GeneratedImageGalleryProps {
  /** 表示中のアカウントID。ダウンロード記録に渡す */
  accountId: string;
  /** 画像生成済みのテーマ・タイトル一覧 */
  items: GeneratedContentImages[];
  /**
   * テーマ・タイトルidをキーにした、投稿文とハッシュタグを1つにまとめた編集可能テキスト。
   * 未生成の間はキーを持たない。後続のTikTok連携に下書きの説明文としてそのまま渡す前提の値
   */
  captionTexts: Map<string, string>;
  /** ハッシュタグ・説明文を生成中のテーマ・タイトルid一覧 */
  generatingCaptionIds: Set<string>;
  /** 「ハッシュタグ・説明文を生成」クリック時のハンドラ */
  onGenerateCaption: (item: GeneratedContentImages) => void;
  /** 投稿文・ハッシュタグのテキストエリアを編集したときのハンドラ */
  onCaptionTextChange: (id: string, text: string) => void;
  /** TikTok連携済みのテーマ・タイトルid一覧 */
  connectedTiktokIds: Set<string>;
  /** TikTok連携中のテーマ・タイトルid一覧 */
  connectingTiktokIds: Set<string>;
  /** 「TikTokと連携」クリック時のハンドラ */
  onConnectTiktok: (item: GeneratedContentImages) => void;
}

/**
 * 画像URLをfetchしてBlobとして取得する。GCS 原本バケットの V4 署名付きURL（別オリジン）を
 * 参照するため、data URL 時代の `decodeURIComponent` 直接デコードは使えない
 * （PR0 で GCS バケットに GET の CORS 設定済みのため、ここでの fetch は preflight を通過する）。
 *
 * @graph-connects gcs [reads_from, via:fetch] GCS 原本バケットの V4 署名付きURLから画像を取得
 */
async function fetchImageBlob(imageUrl: string): Promise<Blob> {
  const response = await fetch(imageUrl);
  return response.blob();
}

/**
 * Blobをブラウザのダウンロードとして保存する。`<a download>` はクロスオリジンのURLでは
 * 無視されて遷移するだけになるため、`URL.createObjectURL()` で same-origin の blob: URL を
 * 作ってから使う。
 *
 * @graph-connects none
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

/**
 * 1件のテーマ・タイトルが持つ全ページ画像をZIPにまとめ、ブラウザのダウンロードとして保存する。
 *
 * @graph-connects none
 */
async function downloadItemAsZip(item: GeneratedContentImages): Promise<void> {
  const zip = new JSZip();
  for (const page of item.pages) {
    zip.file(`${page.pageNumber.replace('/', '-')}.png`, await fetchImageBlob(page.imageUrl));
  }
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(zipBlob, `${item.theme}.zip`);
}

/**
 * 1ページぶんの画像をダウンロードする。
 *
 * @graph-connects none
 */
async function downloadSinglePage(theme: string, page: GeneratedImagePage): Promise<void> {
  const blob = await fetchImageBlob(page.imageUrl);
  triggerBlobDownload(blob, `${theme}_${page.pageNumber.replace('/', '-')}.png`);
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business 「全て画像生成する」実行後の結果表示。テーマ・タイトルごとにページ画像を横並びで表示し、各ページを個別に、
 * またはテーマ・タイトルまとめてZIPでダウンロードできる。その下でハッシュタグ・説明文の生成、生成後のTikTok連携までを1件ずつ進められる
 * @graph-connects none
 */
export function GeneratedImageGallery({
  accountId,
  items,
  captionTexts,
  generatingCaptionIds,
  onGenerateCaption,
  onCaptionTextChange,
  connectedTiktokIds,
  connectingTiktokIds,
  onConnectTiktok,
}: GeneratedImageGalleryProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadingPageKey, setDownloadingPageKey] = useState<string | null>(null);
  const recordContentDownloadMutation = useRecordContentDownload();

  if (items.length === 0) return null;

  /**
   * ダウンロードが実際に使われたことを記録する。保存に失敗してもダウンロード自体の
   * UXを妨げてはいけないため、ここでの例外は握りつぶし、ログにだけ残す。
   */
  const recordDownload = (contentId: string) => {
    recordContentDownloadMutation.mutate(
      { accountId, contentId },
      {
        onError: (error: unknown) => {
          logError(error instanceof Error ? error : new Error(String(error)), {
            event: 'sns_content_generator_record_download_failed',
          });
        },
      },
    );
  };

  const handleDownloadAll = (item: GeneratedContentImages) => {
    setDownloadingId(item.id);
    downloadItemAsZip(item)
      .then(() => {
        recordDownload(item.contentId);
      })
      .catch((error: unknown) => {
        showToast('error', t('snsContentGenerator.downloadAllError'), 5000);
        logError(error instanceof Error ? error : new Error(String(error)), {
          event: 'sns_content_generator_zip_download_failed',
        });
      })
      .finally(() => {
        setDownloadingId(null);
      });
  };

  const handleDownloadSingle = (item: GeneratedContentImages, page: GeneratedImagePage) => {
    const pageKey = `${item.id}:${page.pageNumber}`;
    setDownloadingPageKey(pageKey);
    downloadSinglePage(item.theme, page)
      .then(() => {
        recordDownload(item.contentId);
      })
      .catch((error: unknown) => {
        showToast('error', t('snsContentGenerator.downloadImageError'), 5000);
        logError(error instanceof Error ? error : new Error(String(error)), {
          event: 'sns_content_generator_single_download_failed',
        });
      })
      .finally(() => {
        setDownloadingPageKey((current) => (current === pageKey ? null : current));
      });
  };

  return (
    <div className="space-y-6">
      {items.map((item) => {
        const captionText = captionTexts.get(item.id);
        const isConnected = connectedTiktokIds.has(item.id);

        return (
          <div
            key={item.id}
            className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{item.theme}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{item.title}</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  handleDownloadAll(item);
                }}
                loading={downloadingId === item.id}
              >
                {t('snsContentGenerator.downloadAllImages')}
              </Button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {item.pages.map((page) => (
                <div key={page.pageNumber} className="shrink-0 space-y-1.5">
                  <img
                    src={page.imageUrl}
                    alt={page.pageNumber}
                    className="w-28 h-36 rounded-lg border border-[var(--color-border)] object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleDownloadSingle(item, page);
                    }}
                    disabled={downloadingPageKey === `${item.id}:${page.pageNumber}`}
                    className="block w-full text-center text-xs text-[var(--color-accent-primary)] hover:underline disabled:opacity-50"
                  >
                    {downloadingPageKey === `${item.id}:${page.pageNumber}`
                      ? t('snsContentGenerator.downloadingImage')
                      : t('snsContentGenerator.downloadImage')}
                  </button>
                </div>
              ))}
            </div>

            {captionText !== undefined ? (
              <div className="space-y-2 rounded-lg bg-[var(--color-bg-elevated)] p-3">
                <div>
                  <label
                    htmlFor={`sns-content-generator-caption-${item.id}`}
                    className="text-xs font-medium text-[var(--color-text-muted)]"
                  >
                    {t('snsContentGenerator.captionLabel')}
                  </label>
                  <textarea
                    id={`sns-content-generator-caption-${item.id}`}
                    value={captionText}
                    onChange={(e) => {
                      onCaptionTextChange(item.id, e.target.value);
                    }}
                    rows={4}
                    className="mt-1 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]"
                  />
                </div>
                <div className="flex justify-end">
                  {isConnected ? (
                    <Badge variant="success" size="sm">
                      {t('snsContentGenerator.tiktokConnected')}
                    </Badge>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        onConnectTiktok(item);
                      }}
                      loading={connectingTiktokIds.has(item.id)}
                    >
                      {connectingTiktokIds.has(item.id)
                        ? t('snsContentGenerator.connectingTiktok')
                        : t('snsContentGenerator.connectTiktok')}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onGenerateCaption(item);
                  }}
                  loading={generatingCaptionIds.has(item.id)}
                >
                  {generatingCaptionIds.has(item.id)
                    ? t('snsContentGenerator.generatingCaption')
                    : t('snsContentGenerator.generateCaption')}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
